/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import moment from 'moment';
import Loader from 'react-js-loader';
import { MdArrowDropDownCircle } from 'react-icons/md';
import { IoMdArrowDropupCircle } from "react-icons/io";
import { IoMdCloseCircle } from "react-icons/io";
import { Tooltip } from 'react-tooltip';
import { MdInfoOutline } from "react-icons/md";

interface TripTableProps {
  tripData: any[];
  isLoading: boolean;
  isLoadMore: boolean;
  error: boolean;
  renderGarbageTypes: (trip: any) => string;
  t: (key: string) => string;
}

const TripTable: React.FC<TripTableProps> = ({ tripData, isLoading, isLoadMore, error, renderGarbageTypes, t }) => {
  const [expandedBreaks, setExpandedBreaks] = useState<number | null>(null);

  const toggleBreaksExpansion = (index: number) => {
    if (expandedBreaks === index) {
      setExpandedBreaks(null);
    } else {
      setExpandedBreaks(index);
    }
  };

  const calculateTotalBreakTime = (trip: any) => {
    if (!trip.break_start_time || !trip.break_end_time) {
      return '-';
    }

    const breakStarts = trip.break_start_time.split(',').map((time: string) => time.trim());
    const breakEnds = trip.break_end_time.split(',').map((time: string) => time.trim());

    const minLength = Math.min(breakStarts.length, breakEnds.length);
    let totalBreakMinutes = 0;

    for (let i = 0; i < minLength; i++) {
      if (breakStarts[i] && breakEnds[i]) {
        const breakStart = moment(breakStarts[i], 'HH:mm');
        const breakEnd = moment(breakEnds[i], 'HH:mm');

        let breakMins = breakEnd.diff(breakStart, 'minutes');
        if (breakMins < 0) {
          breakMins = breakEnd.add(1, 'days').diff(breakStart, 'minutes');
        }

        totalBreakMinutes += breakMins;
      }
    }
    const hours = Math.floor(totalBreakMinutes / 60);
    const minutes = totalBreakMinutes % 60;

    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const calculateBreakMinutes = (trip: any) => {
    if (!trip.break_start_time || !trip.break_end_time) {
      return 0;
    }

    const breakStarts = trip.break_start_time.split(',').map((time: string) => time.trim());
    const breakEnds = trip.break_end_time.split(',').map((time: string) => time.trim());

    const minLength = Math.min(breakStarts.length, breakEnds.length);
    let totalBreakMinutes = 0;

    for (let i = 0; i < minLength; i++) {
      if (breakStarts[i] && breakEnds[i]) {
        const breakStart = moment(breakStarts[i], 'HH:mm');
        const breakEnd = moment(breakEnds[i], 'HH:mm');

        let breakMins = breakEnd.diff(breakStart, 'minutes');
        if (breakMins < 0) {
          breakMins = breakEnd.add(1, 'days').diff(breakStart, 'minutes');
        }

        totalBreakMinutes += breakMins;
      }
    }

    return totalBreakMinutes;
  };

  const calculateTotalWorkMinutes = (trip: any) => {
    if (!trip.check_in_time || !trip.check_out_time) {
      return 0;
    }

    const startTime = moment(trip.check_in_time);
    const endTime = moment(trip.check_out_time);

    let totalMinutes = endTime.diff(startTime, 'minutes');
    if (totalMinutes < 0) {
      totalMinutes = endTime.add(1, 'days').diff(startTime, 'minutes');
    }

    return totalMinutes;
  };

  const formatTimeString = (minutes: number) => {
    const hours = Math.floor(Math.abs(minutes) / 60);
    const mins = Math.abs(minutes) % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  const calculateDailyNetWorkingTime = (trip: any, index: number) => {
    if (!trip.check_in_time || !trip.check_out_time) {
      return '-';
    }

    const startTime = moment(trip.check_in_time);
    const endTime = moment(trip.check_out_time);

    const startDate = startTime.format('YYYY-MM-DD');
    const endDate = endTime.format('YYYY-MM-DD');

    const totalMinutes = calculateTotalWorkMinutes(trip);
    const breakDuration = calculateBreakMinutes(trip);
    const netMinutes = totalMinutes - breakDuration;

    const hours = Math.floor(Math.abs(netMinutes) / 60);
    const minutes = Math.abs(netMinutes) % 60;
    const formattedTime = `${netMinutes < 0 ? '-' : ''}${hours}:${minutes.toString().padStart(2, '0')}`;

    const daySpan = startDate !== endDate ? ` (${moment(startDate).format('DD/MM')} - ${moment(endDate).format('DD/MM')})` : '';

    if (netMinutes < 0) {
      return (
        <div
          id={`negative-time-${index}`}
          data-tooltip-id={`tooltip-${index}`}
          className="d-flex align-items-center justify-content-center"
        >
          <span style={{ color: 'red' }}>{formattedTime}{daySpan}</span>
          <MdInfoOutline
            size={16}
            color="gray"
            className="ms-1"
            // style={{ cursor: 'help' }}
          />
          <Tooltip id={`tooltip-${index}`} place="top">
            {t('Total shift')}: {formatTimeString(totalMinutes)} ({trip.check_in_time.slice(0, 5)} - {trip.check_out_time.slice(0, 5)})
            <br />
            {t('Total breaks')}: {formatTimeString(breakDuration)}
            <br />
            {t('Break time exceeds working time by')}: {formatTimeString(Math.abs(netMinutes))}
          </Tooltip>
        </div>
      );
    }

    return `${formattedTime}${daySpan}`;
  };

  const formatStartEndTime = (trip: any) => {
    if (!trip.check_in_time && !trip.check_out_time) {
      return '-';
    }

    const startFormatted = trip.check_in_time
      ? moment(trip.check_in_time).format("HH:mm")
      : '-';

    const endFormatted = trip.check_out_time
      ? moment(trip.check_out_time).format("HH:mm")
      : '-';

    return `${startFormatted} - ${endFormatted}`;
  };

  const hasBreakData = (trip: any) => {
    return trip.break_start_time && trip.break_end_time &&
      trip.break_start_time.trim() !== '' && trip.break_end_time.trim() !== '';
  };

  return (
    <div className="relative">
      <div className="table-container">
        <table className="table-responsive">
          <thead className="tableHeading">
            <tr>
              <th className="action col-1 items">{t('Sno')}</th>
              <th className="col-1 items">{t('userName')}</th>
              <th className="col-1 items">{t('First_Name')}</th>
              <th className="col-1 items">{t('co_driver')}</th>
              <th className="col-1 items">{t('Category')}</th>
              <th className="col-1 items">{t('sub_category')}</th>
              <th className="col-1 action items">{t('Tour_Id')}</th>
              <th className="col-1 providerName items">{t('garbageType')}</th>
              <th className="col-1 items">{t('VehicleNo')}</th>
              <th className="col-1 items">{t('Start_End_Time')}</th>
              <th className="col-1 items">{t('Total_Break_Time')}</th>
              <th className="col-1 items">{t('net_Working_Time')}</th>
              <th className="col-1 items">{t('PhoneNo')}</th>
              <th className="col-1 items action">{t('diesel_filled_up')}</th>
              <th className="col-1 items action">{t('Location')}</th>
              <th className="col-1 items">{t('postal_code')}</th>
              <th className="col-1 items">{t('start_mileage')}</th>
              <th className="col-1 items">{t('final_mileage')}</th>
              <th className="col-1 items">{t("km_driven")}</th>
              <th className="col-1 items">{t('AdBlue')}</th>
              <th className="col-1 items">{t('accident')}</th>
            </tr>
          </thead>
          <tbody className="tableBody">
            {isLoading ? (
              <tr>
                <td colSpan={22} className="text-white">
                  <Loader
                    type="bubble-top"
                    bgColor={'#05653F'}
                    color={'#05653F'}
                    size={50}
                  />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={22} className="text-white">
                  {t('errorLoadingData')}
                </td>
              </tr>
            ) : Array.isArray(tripData) && tripData?.length > 0 ? (
              tripData.map((trip, index) => (
                <tr key={index}>
                  <td className="action col-1 items">{index + 1}</td>
                  <td className="col-1 action items">{trip.username || '-'}</td>
                  <td className="col-1 items">{trip.firstname && trip.lastname ? `${trip.firstname} ${trip.lastname}` : '-'}</td>
                  <td className="col-1 items">
                    {trip.co_driver ? trip.co_driver.split(',').join(', ') : '-'}
                  </td>
                  <td className="col-1 items">{trip.category || '-'}</td>
                  <td className="col-1 items">{trip.sub_category || '-'}</td>
                  <td className="col-1 action items">{trip.trip_id || '-'}</td>
                  <td className="col-1 items">{renderGarbageTypes(trip) || '-'}</td>
                  <td className="col-1 items">{trip.vehicleNumber || '-'}</td>
                  <td className="col-1 items">{formatStartEndTime(trip)}</td>
                  <td className="col-1 items">
                    {calculateTotalBreakTime(trip)}
                    {hasBreakData(trip) && (
                      <button
                        onClick={() => toggleBreaksExpansion(index)}
                        className="ml-2 text-blue-500 hover:text-blue-700 focus:outline-none postition-abouslte"
                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        {expandedBreaks === index ? <MdArrowDropDownCircle size={20} color='#07754d' />
                          : <IoMdArrowDropupCircle size={20} color='#07754d' />}
                      </button>
                    )}
                  </td>
                  <td className="col-1 items">{calculateDailyNetWorkingTime(trip, index)}</td>
                  <td className="col-1 items">{trip.phonenumber || '-'}</td>
                  <td className="col-1 items action">{trip.diesel_filled_up || '-'}</td>
                  <td className="col-1 items action">{trip.location || '-'}</td>
                  <td className="col-1 items">{trip.postal_code || '-'}</td>
                  <td className="col-1 items">{trip.start_mileage || '-'}</td>
                  <td className="col-1 items">{trip.final_mileage || '-'}</td>
                  <td className="col-1 items">
                    {trip.final_mileage && trip.start_mileage
                      ? Number(trip.final_mileage) - Number(trip.start_mileage)
                      : '-'}
                  </td>
                  <td className="col-1 items">{trip.reFuel || '-'}</td>
                  <td className="col-1 items">
                    {trip.damaged === 0 ? t('No') : trip.damaged === 1 ? t('Yes') : '-'}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={22} className="text-white">
                  {t('noData')}
                </td>
              </tr>
            )}
            {isLoadMore ? (
              <tr>
                <td colSpan={22} className="text-white">
                  <Loader
                    type="bubble-top"
                    bgColor={'#05653F'}
                    color={'#05653F'}
                    size={50}
                  />
                </td>
              </tr>) : null}
          </tbody>
        </table>
      </div>

      {expandedBreaks !== null && tripData[expandedBreaks] && hasBreakData(tripData[expandedBreaks]) && (
        <div className="fixed-break-details">
          <button
            className="close-button"
            onClick={() => setExpandedBreaks(null)}
          >
            <IoMdCloseCircle color='#05653F' size={20} />
          </button>

          <h3 className="card-title text-center border-bottom pb-2 fw-bold">{t('Break_Details')}</h3>
          <div className="row justify-content-center">
            {(() => {
              const trip = tripData[expandedBreaks];
              const breakStarts = trip.break_start_time.split(',').map((time: string) => time.trim());
              const breakEnds = trip.break_end_time.split(',').map((time: string) => time.trim());
              const minLength = Math.min(breakStarts.length, breakEnds.length);

              return Array.from({ length: minLength }).map((_, i) => (
                <div key={i} className="col-md-6 col-lg-4 mb-3">
                  <div className="card border-light shadow-sm break-card break-details-container">
                    <div className="card-body d-flex flex-column align-items-center">
                      <span className="badge bg-primary text-white fs-6 px-3 py-1 mb-2">
                        {t('break')} {i + 1}
                      </span>

                      <div className="text-center">
                        <span className="text-muted">{breakStarts[i].slice(0, 5)} - {breakEnds[i].slice(0, 5)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default TripTable;